import { z } from "zod";

/**
 * Shared DTO cho Admin Integrity Oversight (US-AE5.F4-0020):
 *  - @UC9043 idempotency-oversight — GET /admin/idempotency-keys (hit rate + nghi vấn replay).
 *  - @UC9044 global-cash-flow   — GET /admin/cash-flow (tổng vào/ra toàn hệ + spike).
 *
 * Một nguồn sự thật shape cho api (S01/S02) + admin web (S04) — tránh drift DTO ở 2 nơi (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - B1: tiền = BigInt minor units serialize thành STRING trên wire (không float/number JS mất chính
 *   xác). Field money redact-được (inflow/outflow/net) = `string | null` (redaction theo vai — chỉ
 *   SUPER_ADMIN/OPS xem tiền). `hitRate` = TỈ LỆ 0..1 (KHÔNG phải tiền → number thường).
 * - `.strict()` — KHÔNG khai báo field lạ; reject tại biên (chống vô tình leak).
 * - KHÔNG nhúng `responseBody` thô (idempotency) — tránh leak PII trong response payload.
 * - Cursor opaque string — đối xứng common/pagination/cursor.
 * - G2: DTO chỉ mô tả kết quả ĐỌC (idempotency append-only quan sát + cash-flow đọc ledger);
 *   KHÔNG có field mutating ledger / idempotency.
 */

/** Max page-size cho bảng idempotency. Default (20) do backend áp khi vắng. */
export const ADMIN_IDEMPOTENCY_MAX_LIMIT = 100;

/**
 * Lý do nghi vấn replay (AC2):
 *  - `cross_endpoint`: cùng `key` xuất hiện ở >1 endpoint (khả năng replay xuyên route).
 *  - `hash_mismatch`: cùng (key,endpoint) nhưng `requestHash` khác request gốc (payload đổi).
 *  - `cross_actor`: cùng `key` liên kết >1 actor (suy từ ledger_transactions.createdBy).
 */
export const idempotencySuspectReasonSchema = z.enum([
  "cross_endpoint",
  "hash_mismatch",
  "cross_actor",
]);
export type IdempotencySuspectReason = z.infer<typeof idempotencySuspectReasonSchema>;

/**
 * Actor (người thực hiện) — suy từ ledger_transactions.createdBy. PII (id người dùng) → redact theo
 * vai: SUPPORT/READ_ONLY → null. `null` khi không suy được actor (không có ledger tx liên kết key).
 */
export const idempotencyActorSchema = z
  .object({
    userId: z.string(),
  })
  .strict()
  .nullable();
export type IdempotencyActor = z.infer<typeof idempotencyActorSchema>;

/**
 * Một dòng idempotency (UC9043 AC1/AC2). KHÔNG có `responseBody` thô (chỉ status).
 * `hits` = số op tài chính gắn với key (COUNT ledger_transactions có idempotencyKey = key). KHÔNG
 * phải số lần replay HTTP — là đại lượng quan sát số giao dịch mà key điều khiển (thường 1; >1 khi
 * key dùng ở nhiều quỹ; 0 khi key không gắn ledger, vd vote).
 */
export const idempotencyRowSchema = z
  .object({
    key: z.string(),
    endpoint: z.string(),
    /** null nếu request gốc không lưu hash. */
    requestHash: z.string().nullable(),
    /** HTTP status của response gốc được tái dùng (null nếu chưa lưu). */
    responseStatus: z.number().int().nullable(),
    /** Actor suy từ ledger (PII → redact theo vai). */
    actor: idempotencyActorSchema,
    /** Số op tài chính (ledger tx) gắn với key (≥0) — KHÔNG phải số lần replay HTTP. */
    hits: z.number().int().min(0),
    /** true nếu phát hiện dấu hiệu nghi vấn replay. */
    suspectReplay: z.boolean(),
    /** Lý do nghi vấn (chỉ có khi suspectReplay=true). */
    suspectReason: idempotencySuspectReasonSchema.optional(),
    createdAt: z.string(),
  })
  .strict();
export type IdempotencyRow = z.infer<typeof idempotencyRowSchema>;

/** Query GET /admin/idempotency-keys — filter endpoint/suspectOnly + cursor pagination. */
export const idempotencyQuerySchema = z
  .object({
    endpoint: z.string().max(256).optional(),
    /** Chỉ trả các dòng nghi vấn replay. Coerce 'true'/'false' từ query string. */
    suspectOnly: z
      .preprocess((v) => (v === "true" ? true : v === "false" ? false : v), z.boolean())
      .optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_IDEMPOTENCY_MAX_LIMIT).optional(),
  })
  .strict();
export type IdempotencyQuery = z.infer<typeof idempotencyQuerySchema>;

/** Summary trên đầu bảng idempotency (UC9043 AC1). hitRate = tỉ lệ 0..1 (KHÔNG phải tiền). */
export const idempotencySummarySchema = z
  .object({
    totalKeys: z.number().int().min(0),
    hitCount: z.number().int().min(0),
    /** Tỉ lệ hit = hitCount / totalKeys (0..1). Number thường — KHÔNG money. */
    hitRate: z.number().min(0).max(1),
    suspectCount: z.number().int().min(0),
  })
  .strict();
export type IdempotencySummary = z.infer<typeof idempotencySummarySchema>;

/** Envelope bảng idempotency `{ data, pageInfo, summary }`. */
export const idempotencyResponseSchema = z
  .object({
    data: z.array(idempotencyRowSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    summary: idempotencySummarySchema,
  })
  .strict();
export type IdempotencyResponse = z.infer<typeof idempotencyResponseSchema>;

// ── Cash flow (@UC9044) ────────────────────────────────────────────────────

/** Kỳ tổng hợp dòng tiền. */
export const cashFlowPeriodSchema = z.enum(["day", "week", "month"]);
export type CashFlowPeriod = z.infer<typeof cashFlowPeriodSchema>;

/**
 * Money string trên wire = BigInt minor units (integer). Reject float (AC4) & rác. inflow/outflow
 * luôn ≥0; net có thể âm (outflow > inflow) → cho dấu '-'. `null` khi redact theo vai.
 */
const moneyMinorString = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)");
const moneyMinorNullable = moneyMinorString.nullable();

/** Query GET /admin/cash-flow — period + range (ISO date). */
export const cashFlowQuerySchema = z
  .object({
    period: cashFlowPeriodSchema.default("month"),
    from: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "from phải là YYYY-MM-DD")
      .optional(),
    to: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "to phải là YYYY-MM-DD")
      .optional(),
  })
  .strict();
export type CashFlowQuery = z.infer<typeof cashFlowQuerySchema>;

/**
 * Một bucket dòng tiền theo kỳ (UC9044 AC1). Money = STRING minor | NULL (redact).
 * `spikeFlag` = true khi biến động vượt ngưỡng config vs kỳ trước (AC2).
 */
export const cashFlowBucketSchema = z
  .object({
    /** Mốc kỳ (ISO date YYYY-MM-DD, đầu bucket). */
    bucket: z.string(),
    inflowMinor: moneyMinorNullable,
    outflowMinor: moneyMinorNullable,
    netMinor: moneyMinorNullable,
    spikeFlag: z.boolean(),
  })
  .strict();
export type CashFlowBucket = z.infer<typeof cashFlowBucketSchema>;

/** Envelope cash-flow `{ buckets, totalInflowMinor, totalOutflowMinor, spikeThresholdPct }`. */
export const cashFlowResponseSchema = z
  .object({
    period: cashFlowPeriodSchema,
    buckets: z.array(cashFlowBucketSchema),
    totalInflowMinor: moneyMinorNullable,
    totalOutflowMinor: moneyMinorNullable,
    /**
     * Ngưỡng spike (% tăng vs kỳ trước) đọc từ system_settings; null nếu chưa cấu hình
     * (spikeFlag luôn false). KHÔNG hardcode ở client.
     */
    spikeThresholdPct: z.number().nullable(),
  })
  .strict();
export type CashFlowResponse = z.infer<typeof cashFlowResponseSchema>;
